import React, { useState, useEffect, useContext, useRef } from 'react';
import CloseIcon from "@mui/icons-material/Close";
import {
    CircularProgress,
} from '@mui/material';

import { InputSelector } from "../../../components/UI/InputSelector";
import { DropdownSelector } from "../../../components/UI/DropdownSelector";

import { EditorState, convertToRaw, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import styles from "../../../styles/pages/DepositMethods/components/DepositMethodModal.module.css"
import { LangContext } from "../../../context/LangContext";
import { ThemeContext } from "../../../context/ThemeContext";

const translations = {
    ru: {
        title: "Переопределение метода",
        confirm: "Сохранить переопределение",
        cancel: "Отмена",
        methodFromList: "Метод из списка",
        methodKey: "Ключ метода",
        label: "Название",
        type: "Тип",
        sort: "Сортировка",
        address: "Адрес (Override)",
        contentHtml: "Содержание HTML (Override)",
        isActive: "Активность (Override)",
        isHidden: "Скрыт (Override)",
        visible: "Виден",
        hidden: "Скрыт",
        icon: "Иконка (Override)",
        currentIcon: "Текущая иконка",
    },
    en: {
        title: "Method Override",
        confirm: "Save Override",
        cancel: "Cancel",
        methodFromList: "Method from list",
        methodKey: "Method key",
        label: "Label",
        type: "Type",
        sort: "Sort",
        address: "Address (Override)",
        contentHtml: "Content HTML (Override)",
        isActive: "Is Active (Override)",
        isHidden: "Is Hidden (Override)",
        visible: "Visible",
        hidden: "Hidden",
        icon: "Icon (Override)",
        currentIcon: "Current Icon",
    },
};

const ALLOWED_TYPES = [
    { value: 'crypto', label: 'Криптовалюта' },
    { value: 'bank', label: 'Банк' },
    { value: 'card_gateway', label: 'Card Gateway' },
];

export const OverrideDepositMethodModal = ({
    open,
    handleClose,
    handleSave,
    isSubmitting,
    initialData
}) => {

    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const t = translations[langSelected] || translations.ru;

    const isEditing = !!initialData;
    const modalRef = useRef(null);

    const methodKey = initialData?.key || initialData?.method_key || '';
    const initialLabel = initialData?.label || '';
    const methodOptions = (initialData?.methodOptions || []).map((option) => ({
        value: option.key,
        label: option.label ? `${option.label} (${option.key})` : option.key,
        meta: option,
    }));

    const [editorState, setEditorState] = useState(EditorState.createEmpty());

    const [formData, setFormData] = useState({
        method_key: methodKey,
        type: initialData?.type || 'crypto',
        label: initialData?.label || '',
        sort: initialData?.sort ?? 0,
        address: initialData?.address || '',
        is_active: initialData?.is_active !== undefined ? initialData.is_active : true,
        is_hidden: initialData?.is_hidden !== undefined ? initialData.is_hidden : false,
        icon: null,
        icon_preview: initialData?.icon || '',
    });
    const isCardGateway = formData.type === 'card_gateway';

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!open || !initialData) return;

        setFormData({
            method_key: initialData.key || initialData.method_key || '',
            type: initialData.type || 'crypto',
            label: initialData.label || '',
            sort: initialData.sort ?? 0,
            address: initialData.address || '',
            is_active: initialData.is_active !== undefined ? initialData.is_active : true,
            is_hidden: initialData.is_hidden !== undefined ? initialData.is_hidden : false,
            icon: null,
            icon_preview: initialData.icon || '',
        });
        setErrors({});

        const contentHtml = initialData.content_html || initialData.html || '';
        if (contentHtml) {
            const contentBlock = htmlToDraft(contentHtml);
            if (contentBlock) {
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
                setEditorState(EditorState.createWithContent(contentState));
            }
        } else {
            setEditorState(EditorState.createEmpty());
        }
    }, [initialData, open]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                handleClose();
            }
        };

        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, handleClose]);

    const handleChange = (key, value) => {
        if (key === 'method_key') {
            const selectedOption = methodOptions.find((option) => option.value === value);

            if (selectedOption?.meta) {
                setFormData((prev) => ({
                    ...prev,
                    method_key: value,
                    type: selectedOption.meta.type || prev.type,
                    label: selectedOption.meta.label || prev.label,
                    sort: selectedOption.meta.sort ?? prev.sort,
                    is_hidden: selectedOption.meta.is_hidden ?? prev.is_hidden,
                    icon_preview: selectedOption.meta.icon || prev.icon_preview,
                }));

                return;
            }
        }

        setFormData((prev) => ({ ...prev, [key]: value }));
    };

    const handleSwitchChange = () => {
        setFormData(prev => ({
            ...prev,
            is_active: !prev.is_active,
        }));
    };

    const handleHiddenSwitchChange = () => {
        setFormData(prev => ({
            ...prev,
            is_hidden: !prev.is_hidden,
        }));
    };

    const handleIconChange = (event) => {
        const file = event.target.files?.[0] || null;

        setFormData((prev) => ({
            ...prev,
            icon: file,
            icon_preview: file ? URL.createObjectURL(file) : prev.icon_preview,
        }));
    };

    const onEditorStateChange = (newEditorState) => {
        setEditorState(newEditorState);
        setErrors(prev => ({ ...prev, content_html: null }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const content_html = isCardGateway ? '' : draftToHtml(convertToRaw(editorState.getCurrentContent()));
        const resolvedMethodKey = methodKey || formData.method_key;

        if (!resolvedMethodKey) {
            setErrors({ general: langSelected === 'ru' ? 'Выберите ключ метода.' : 'Please select method key.' });
            return;
        }

        const finalData = {
            method_key: resolvedMethodKey,
            type: formData.type,
            label: formData.label,
            sort: Number(formData.sort),
            address: isCardGateway ? '' : formData.address,
            content_html: content_html,
            is_active: formData.is_active,
            is_hidden: formData.is_hidden,
            icon: formData.icon,
        };

        handleSave(finalData, resolvedMethodKey);
    };

    const title = t.title;
    const selectedFromList = methodOptions.some((option) => option.value === formData.method_key);
    const isTypeAndKeyLocked = !methodKey && selectedFromList;

    if (!open) return null;

    const modalClass = `${styles.modal} ${theme === 'dark' ? styles.darkModal : ''}`;
    const containerClass = `${styles.filtersContainer} ${theme === 'dark' ? styles.darkFiltersContainer : ''}`;
    const labelClass = `${styles.modalLabel} ${theme === 'dark' ? styles.darkModalLabel : ''}`;
    const footerClass = `${styles.modalFooter} ${theme === 'dark' ? styles.darkModalFooter : ''}`;
    const submitBtnClass = `${styles.submitButton} ${theme === 'dark' ? styles.darkSubmitButton : ''}`;
    const resetWrapClass = `${styles.resetWrapper} ${theme === 'dark' ? styles.darkResetWrapper : ''}`;
    const editorWrapClass = `${styles.editorContainer} ${theme === 'dark' ? styles.darkEditorContainer : ''}`;

    return (
        <div className={modalClass}>

            <div className={containerClass} ref={modalRef}>
                <div className={labelClass}>
                    <p>{title}{initialLabel ? ` (${initialLabel})` : ''}</p>
                    <div onClick={handleClose}><CloseIcon className={theme === 'dark' ? styles.darkCloseIcon : ''} /></div>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalContent}>

                    <div className={styles.section}>
                        {!methodKey && (
                            <div className={styles.inputRow}>
                                <label className={theme === 'dark' ? styles.darkText : ''}>{t.methodFromList}:</label>
                                <DropdownSelector
                                    filterName={'method_key'}
                                    selectedValue={formData.method_key}
                                    handleFilterChange={handleChange}
                                    options={methodOptions}
                                    theme={theme}
                                />
                            </div>
                        )}
                        {!methodKey && (
                            <>
                                <div className={styles.inputRow}>
                                    <label className={theme === 'dark' ? styles.darkText : ''}>{t.methodKey}:</label>
                                <InputSelector
                                    filterName={'method_key'}
                                    selectedValue={formData.method_key}
                                    handleFilterChange={handleChange}
                                    inputType={'text'}
                                    disabled={isTypeAndKeyLocked}
                                    theme={theme}
                                />
                            </div>
                                <div className={styles.inputRow}>
                                    <label className={theme === 'dark' ? styles.darkText : ''}>{t.label}:</label>
                                    <InputSelector
                                        filterName={'label'}
                                        selectedValue={formData.label}
                                        handleFilterChange={handleChange}
                                        inputType={'text'}
                                        theme={theme}
                                    />
                                </div>
                                <div className={styles.inputRow}>
                                    <label className={theme === 'dark' ? styles.darkText : ''}>{t.type}:</label>
                                    <DropdownSelector
                                        filterName={'type'}
                                        selectedValue={formData.type}
                                        handleFilterChange={handleChange}
                                        options={ALLOWED_TYPES}
                                        disabled={isTypeAndKeyLocked}
                                        theme={theme}
                                    />
                                </div>
                                <div className={styles.inputRow}>
                                    <label className={theme === 'dark' ? styles.darkText : ''}>{t.sort}:</label>
                                    <InputSelector
                                        filterName={'sort'}
                                        selectedValue={formData.sort}
                                        handleFilterChange={handleChange}
                                        inputType={'number'}
                                        theme={theme}
                                    />
                                </div>
                            </>
                        )}

                        {!isCardGateway && (
                            <div className={styles.inputRow}>
                                <label className={theme === 'dark' ? styles.darkText : ''}>{t.address}:</label>
                                <InputSelector
                                    filterName={'address'}
                                    selectedValue={formData.address}
                                    handleFilterChange={handleChange}
                                    inputType={'text'}
                                    theme={theme}
                                />
                            </div>
                        )}

                        <div className={styles.inputRow}>
                            <label className={theme === 'dark' ? styles.darkText : ''}>{t.icon}:</label>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleIconChange}
                                className={`${styles.fileInput} ${theme === 'dark' ? styles.darkFileInput : ''}`}
                            />
                            {formData.icon_preview && (
                                <div className={styles.iconPreviewRow}>
                                    <span className={theme === 'dark' ? styles.darkText : ''}>{t.currentIcon}:</span>
                                    <img src={formData.icon_preview} alt={t.icon} className={styles.iconPreview} />
                                </div>
                            )}
                        </div>

                        <div className={styles.inputRow}>
                            <label className={theme === 'dark' ? styles.darkText : ''}>{t.isActive}:</label>
                            <div
                                className={`${styles.customSwitch} ${formData.is_active ? styles.isActive : ''} ${theme === 'dark' ? styles.darkSwitch : ''}`}
                                onClick={handleSwitchChange}
                            >
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={formData.is_active}
                                    onChange={handleSwitchChange}
                                    style={{ display: 'none' }}
                                />
                                <span className={styles.switchLabel}>
                                    {formData.is_active ? (langSelected === 'ru' ? "Активен" : "Active") : (langSelected === 'ru' ? "Неактивен" : "Inactive")}
                                </span>
                            </div>
                        </div>

                        <div className={styles.inputRow}>
                            <label className={theme === 'dark' ? styles.darkText : ''}>{t.isHidden}:</label>
                            <div
                                className={`${styles.customSwitch} ${!formData.is_hidden ? styles.isActive : styles.isInactiveSwitch} ${theme === 'dark' ? styles.darkSwitch : ''} ${formData.is_hidden && theme === 'dark' ? styles.darkIsInactiveSwitch : ''}`}
                                onClick={handleHiddenSwitchChange}
                            >
                                <input
                                    type="checkbox"
                                    name="is_hidden"
                                    checked={formData.is_hidden}
                                    onChange={handleHiddenSwitchChange}
                                    style={{ display: 'none' }}
                                />
                                <span className={`${styles.switchLabel} ${formData.is_hidden ? styles.inactiveSwitchLabel : ''} ${formData.is_hidden && theme === 'dark' ? styles.darkIsInactiveLabel : ''}`}>
                                    {formData.is_hidden ? t.hidden : t.visible}
                                </span>
                            </div>
                        </div>

                        {!isCardGateway && (
                            <div className={styles.editorFullWidth}>
                                <label className={theme === 'dark' ? styles.darkText : ''}>{t.contentHtml}:</label>
                                <div className={editorWrapClass}>
                                    {open && (
                                        <Editor
                                            editorState={editorState}
                                            wrapperClassName={styles.editorWrapper}
                                            editorClassName={styles.editorContent}
                                            onEditorStateChange={onEditorStateChange}
                                            toolbar={{
                                                options: ['history', 'inline', 'blockType', 'list', 'textAlign', 'link', 'colorPicker', 'image'],
                                                inline: { inDropdown: false, options: ['bold', 'italic', 'underline', 'strikethrough'] },
                                                list: { inDropdown: false },
                                                textAlign: { inDropdown: false },
                                                link: { inDropdown: false },
                                                history: { inDropdown: false },
                                                colorPicker: { inDropdown: false },
                                                image: {
                                                    urlEnabled: true,
                                                    uploadEnabled: false,
                                                    alignmentEnabled: true,
                                                    defaultSize: { height: 'auto', width: '100%' },
                                                },
                                                blockType: { inDropdown: true },
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        )}

                        {errors.general && (
                            <div className={`${styles.textError} ${theme === 'dark' ? styles.darkTextError : ''}`}>{errors.general}</div>
                        )}
                    </div>

                    <div className={footerClass}>
                        <div className={resetWrapClass}>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={isSubmitting}
                                className={styles.clearButton}
                            >
                                {t.cancel}
                            </button>
                        </div>
                        <div className={styles.confirmationWrapper}>
                            <button
                                type="submit"
                                className={submitBtnClass}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress size={20} color="inherit" /> : t.confirm}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
